import { IAISummaryData, IActionItem, ITranscriptEntry } from './AISummary.types';

const MOCK_DELAY_MS = 800;
const IS_TEST_ENV = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

function delay(ms: number) {
  if (IS_TEST_ENV) return Promise.resolve();
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ---- Mock summary content ----

const MOCK_SHORT_NOTES: Record<string, string> = {
  default:
    '<p>The conversation covered retirement planning aspects like contribution limits for 401(k)s and IRAs, investment options, and the importance of a diversified portfolio.</p>',
};

const MOCK_FULL_NOTES: Record<string, string> = {
  default: '<p>The conversation covered retirement planning aspects like contribution limits for 401(k)s and IRAs, investment options, and the importance of a diversified portfolio.</p><ul><li>They discussed different investment options available within their retirement accounts, such as stocks, bonds, and mutual funds, and stressed the importance of maintaining a diversified portfolio to manage risk and ensure steady growth over time.</li><li>Isabelle explained the benefits of tax-deferred growth in traditional retirement accounts, where taxes are paid upon withdrawal, and the advantages of Roth accounts, where contributions are made after-tax dollars, allowing for tax-free withdrawals in retirement.</li><li>They explored various tax-advantaged accounts beyond the standard 401(k) and IRA options, such as Health Savings Accounts (HSAs) and their potential benefits for long-term savings and tax efficiency.</li><li>Both parties agreed on the importance of staying in touch for ongoing support and future discussions to ensure Matthew\'s financial plans remain on track.</li></ul>',
};

const MOCK_ACTION_ITEMS: IActionItem[] = [
  {
    id: 'mock-action-1',
    content:
      'Review current contribution levels to her 401(k) and IRA accounts.',
  },
  {
    id: 'mock-action-2',
    content:
      "Schedule a meeting with a tax advisor to explore strategies for optimizing Matthew's tax situation.",
  },
  {
    id: 'mock-action-3',
    content:
      'Investigate additional tax-advantaged accounts, such as Health Savings Accounts (HSAs), to enhance the overall retirement savings strategy.',
  },
];

const MOCK_TRANSCRIPT: ITranscriptEntry[] = [
  {
    speakerName: 'Sonali Pritchard',
    speakerEmail: 'sonali.pritchard@example.com',
    timestamp: '40000',
    content:
      'Iterative approaches to corporate strategy foster collaborative thinking to further the people value proposition. Organically grow the holistic world view of disruptive innovation via workplace diversity.',
  },
  {
    speakerName: 'Sofia Gomez',
    speakerEmail: 'sofia.gomez@example.com',
    timestamp: '55000',
    content:
      'It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout.',
  },
  {
    speakerName: 'Sonali Pritchard',
    speakerEmail: 'sonali.pritchard@example.com',
    timestamp: '74000',
    content:
      "However, our objective is to foster a collaborative environment where the focus remains on the content's substance rather than mere aesthetics. This is precisely why we choose to employ a placeholder text that provides a more-or-less normal distribution of letters.",
  },
  {
    speakerName: 'Sofia Gomez',
    speakerEmail: 'sofia.gomez@example.com',
    timestamp: '131000',
    content:
      "We are well aware that the readable content of a page's layout can easily distract a reader, and this has long been an established fact. However, our goal is to cultivate a collaborative atmosphere where our focus lies not on superficial presentation but on meaningful engagement.",
  },
  {
    speakerName: 'Sonali Pritchard',
    speakerEmail: 'sonali.pritchard@example.com',
    timestamp: '176000',
    content:
      'Let us continue working together, leveraging our collective expertise and maintaining a collaborative mindset, to achieve the desired outcomes of our shared endeavors.',
  },
  {
    speakerName: 'Sofia Gomez',
    speakerEmail: 'sofia.gomez@example.com',
    timestamp: '210000',
    content:
      'I agree completely. Now, regarding the retirement planning discussion, I wanted to touch on the contribution limits for 401(k) plans. For 2024, the limit is $23,000 for individuals under 50, with an additional catch-up contribution of $7,500 for those 50 and older.',
  },
  {
    speakerName: 'Sonali Pritchard',
    speakerEmail: 'sonali.pritchard@example.com',
    timestamp: '245000',
    content:
      'That is correct. And for traditional and Roth IRAs, the contribution limit is $7,000, with a $1,000 catch-up for those over 50. It is important for Matthew to maximize these contributions to take full advantage of tax-deferred growth.',
  },
  {
    speakerName: 'Sofia Gomez',
    speakerEmail: 'sofia.gomez@example.com',
    timestamp: '285000',
    content:
      'Absolutely. We should also consider the benefits of Health Savings Accounts. HSAs offer a triple tax advantage: contributions are tax-deductible, growth is tax-free, and withdrawals for qualified medical expenses are also tax-free.',
  },
  {
    speakerName: 'Sonali Pritchard',
    speakerEmail: 'sonali.pritchard@example.com',
    timestamp: '320000',
    content:
      'Great point. The HSA contribution limit for family coverage in 2024 is $8,300. And unlike FSAs, HSA funds roll over year to year, making them an excellent long-term savings vehicle for healthcare costs in retirement.',
  },
  {
    speakerName: 'Sofia Gomez',
    speakerEmail: 'sofia.gomez@example.com',
    timestamp: '355000',
    content:
      'Now let us talk about asset allocation. Given Matthew\'s age and risk tolerance, I would recommend a diversified portfolio with approximately 60% equities and 40% fixed income. Within equities, we should consider a mix of domestic and international exposure.',
  },
  {
    speakerName: 'Sonali Pritchard',
    speakerEmail: 'sonali.pritchard@example.com',
    timestamp: '390000',
    content:
      'I think that is a reasonable starting point. We should also look into low-cost index funds to minimize expense ratios. Studies consistently show that passive investing tends to outperform active management over long time horizons.',
  },
  {
    speakerName: 'Sofia Gomez',
    speakerEmail: 'sofia.gomez@example.com',
    timestamp: '430000',
    content:
      'Agreed. For the fixed income portion, we might consider a ladder of Treasury bonds alongside some investment-grade corporate bonds. This provides both stability and a predictable income stream as Matthew approaches retirement.',
  },
  {
    speakerName: 'Sonali Pritchard',
    speakerEmail: 'sonali.pritchard@example.com',
    timestamp: '465000',
    content:
      'One more thing to discuss is the Roth conversion strategy. If Matthew expects to be in a higher tax bracket in retirement, it might make sense to convert some traditional IRA funds to a Roth IRA now, paying taxes at the current lower rate.',
  },
  {
    speakerName: 'Sofia Gomez',
    speakerEmail: 'sofia.gomez@example.com',
    timestamp: '500000',
    content:
      'That is an excellent strategy, especially given the current tax landscape. We should schedule a follow-up meeting with Matthew\'s tax advisor to run the numbers and determine the optimal conversion amount each year.',
  },
  {
    speakerName: 'Sonali Pritchard',
    speakerEmail: 'sonali.pritchard@example.com',
    timestamp: '535000',
    content:
      'Perfect. Let me summarize the action items: review current contribution levels, schedule the tax advisor meeting, investigate HSA options, and prepare a proposed asset allocation model. I will send a follow-up email with all the details by end of week.',
  },
  {
    speakerName: 'Sofia Gomez',
    speakerEmail: 'sofia.gomez@example.com',
    timestamp: '570000',
    content:
      'Sounds great. Thank you for the thorough discussion, Sonali. I feel confident that we have a solid plan in place. Let us reconnect next month to review progress and make any necessary adjustments.',
  },
];

// ---- Public API (mirrors AISummaryService interface) ----

export async function mockGetContainer(containerId: string): Promise<any> {
  await delay(MOCK_DELAY_MS / 2);

  // Convention: container IDs ending in '-pending' simulate pending state
  if (containerId.endsWith('-pending')) {
    return {
      summaryData: {
        status: 'Pending',
        summaryUrl: '',
        notesUrl: '',
        actionItemsUrl: '',
        transcriptUrl: '',
        summarizeAfterCall: true,
      },
      encryptionKeyUrl: '',
    };
  }

  // Convention: container IDs ending in '-error' simulate error state
  if (containerId.endsWith('-error')) {
    throw new Error('Access denied: User not authorized to view this summary');
  }

  // Default: Active summary
  return {
    summaryData: {
      status: 'Active',
      summaryUrl: `https://mock-aibridge/summaries/${containerId}`,
      notesUrl: `https://mock-aibridge/summaries/${containerId}/notes`,
      actionItemsUrl: `https://mock-aibridge/summaries/${containerId}/action-items`,
      transcriptUrl: `https://mock-aibridge/summaries/${containerId}/transcripts`,
      summarizeAfterCall: true,
    },
    encryptionKeyUrl: `kms://mock-kms/keys/${containerId}`,
    kmsResourceObjectUrl: `kms://mock-kms/resources/${containerId}`,
    aclUrl: `https://mock-acl/acls/${containerId}`,
    forkSessionId: containerId,
    callSessionId: containerId,
    ownerUserId: 'mock-self-user',
    orgId: 'mock-org',
    start: new Date(Date.now() - 3600000).toISOString(),
    end: new Date().toISOString(),
  };
}

export async function mockGetSummary(
  _containerId: string
): Promise<IAISummaryData> {
  await delay(MOCK_DELAY_MS);
  return {
    shortNote: MOCK_SHORT_NOTES.default,
    note: MOCK_FULL_NOTES.default,
    actionItems: MOCK_ACTION_ITEMS,
    transcriptUrl: `https://mock-aibridge/summaries/${_containerId}/transcripts`,
    status: 'ready',
  };
}

export async function mockFetchTranscript(
  _transcriptUrl: string
): Promise<ITranscriptEntry[]> {
  await delay(MOCK_DELAY_MS);
  return MOCK_TRANSCRIPT;
}
